/** @jsxImportSource @emotion/react */

import React from 'react';
import classNames from 'classnames';

export type UDTextType =
  | 'title-huge'
  | 'title-large'
  | 'title-1'
  | 'title-2'
  | 'title-3'
  | 'headline'
  | 'body-text'
  | 'callout'
  | 'subhead'
  | 'footnote'
  | 'caption-1'
  | 'caption-2'
  | 'tip';

export type UDTextNewType =
  | 'title-1'
  | 'title-2'
  | 'title-3'
  | 'title-4'
  | 'subtitle-2'
  | 'subtitle-3'
  | 'subtitle-4'
  | 'subtitle-5'
  | 'body-1'
  | 'body-2'
  | 'body-3'
  | 'text-1'
  | 'label-1'
  | 'mini-bold'
  | 'big-size';

const componentsMap: Record<UDTextType, keyof JSX.IntrinsicElements> = {
  'title-huge': 'h1',
  'title-large': 'h1',
  'title-1': 'h1',
  'title-2': 'h2',
  'title-3': 'h3',
  headline: 'h1',
  'body-text': 'p',
  callout: 'p',
  subhead: 'p',
  footnote: 'p',
  'caption-1': 'p',
  'caption-2': 'p',
  tip: 'p',
};

const newComponentsMap: Record<UDTextNewType, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p'> = {
  'title-1': 'h1',
  'title-2': 'h3',
  'title-3': 'h2',
  'title-4': 'h1',
  'subtitle-2': 'p',
  'subtitle-3': 'p',
  'subtitle-4': 'p',
  'subtitle-5': 'p',
  'body-1': 'p',
  'body-2': 'p',
  'body-3': 'p',
  'text-1': 'p',
  'label-1': 'p',
  'mini-bold': 'p',
  'big-size': 'p',
};

type ComponentProps =
  | React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>
  | React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>;

type CommonProps = {
  maxWidth?: number;
  children: React.ReactNode;
  color?: string | 'inherit' | undefined;
  center?: boolean;
} & Omit<ComponentProps, 'children'>;

export type Props =
  | ({
      isNew?: false;
      type: UDTextType;
    } & CommonProps)
  | ({
      isNew: true;
      type: UDTextNewType;
    } & CommonProps);

const UDText = React.forwardRef<HTMLElement, Props>((props, ref) => {
  const { type, children, className, color, maxWidth, isNew = false, center, ...otherProps } = props;

  const additionalStyle = maxWidth
    ? {
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
    : {};

  const Component = (
    isNew ? newComponentsMap[type as UDTextNewType] : componentsMap[type as UDTextType]
  ) as keyof JSX.IntrinsicElements;

  return React.createElement(
    Component,
    {
      ...otherProps,
      ref,
      className: classNames({ text: !isNew, 'text-new': isNew }, type, className, { center }),
      style: { color, ...additionalStyle, ...otherProps.style },
    },
    children,
  );
});

export default UDText;
